import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { serviceDisplayName } from "@/lib/services";
import { ArrowLeft, Plus, Trash2, CheckCircle2, AlertTriangle, XCircle, Send, Info, ChevronDown, X } from "lucide-react";

const CATEGORIE = ["Comunale", "Scuole Superiori", "Esterno", "Privato"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Servizi che, nel modulo Utenti, prevedono la selezione di un utente/gruppo.
// Stesso criterio del form singolo (multiservice.filterOptions in OfficeHome).
const isUserService = (s) => s.entity_kind === "school_user" || s.slug === "centro_estivo";

const parseHours = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

// Deriva le colonne dai servizi selezionati riusando ESATTAMENTE la logica condizionale
// del form singolo (UtentiSection). Colonne comuni una sola volta; specifiche additive.
function deriveColumns(services) {
  const has = (s) => services.includes(s);
  const nido = has("nido"), ce = has("centro_estivo");
  const ass = has("assistenza_scolastica"), adm = has("adm"), rest = has("rest");
  const cols = [];
  cols.push({ key: "nome", label: "Nome", kind: "text", required: true });
  if (!nido) cols.push({ key: "cognome", label: "Cognome", kind: "text",
    required: (r) => !(ce && r.ce_tipologia === "gruppo") });
  if (!nido) cols.push({ key: "comune", label: "Comune", kind: "text", required: !ce });
  if (ass) cols.push({ key: "categoria", label: "Categoria di rendicontazione", kind: "select", options: CATEGORIE, required: true });
  if (ass) cols.push({ key: "school_id", label: "Scuola collegata", kind: "relational", relKind: "schools", required: true });
  if (adm) cols.push({ key: "mail_privata_adm", label: "Mail privata ADM", kind: "email", required: true });
  if (ass) cols.push({ key: "mo_assistenza_scolastica", label: "Monte ore Assistenza", kind: "number", required: false });
  if (adm) cols.push({ key: "mo_adm", label: "Monte ore ADM", kind: "number", required: false });
  if (rest) cols.push({ key: "mo_rest", label: "Monte ore ReST", kind: "number", required: false });
  if (ce) {
    cols.push({ key: "ce_tipologia", label: "Tipologia (Centro Estivo)", kind: "select",
      options: [{ v: "utente", l: "Utente" }, { v: "gruppo", l: "Gruppo" }], required: true });
    cols.push({ key: "ce_site_id", label: "Sede (Centro Estivo)", kind: "relational", relKind: "ce_sites", required: true });
    cols.push({ key: "ce_monte_ore", label: "Monte ore (Centro Estivo)", kind: "number", required: false });
  }
  return cols;
}

const isPasteKind = (k) => k === "text" || k === "email" || k === "number";
const isDropdownKind = (k) => k === "select" || k === "relational";

const emptyRow = () => ({ status: "", error: "", _sel: false });

// Multi-select servizi (Popover + checkbox), coerente con Orbit.
export function ServicesPicker({ value, onChange, options, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const toggle = (slug) => onChange(value.includes(slug) ? value.filter((x) => x !== slug) : [...value, slug]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={testid}
          className={`w-full min-h-11 px-2 py-1.5 rounded-md border text-left text-sm bg-white flex flex-wrap gap-1 items-center transition-colors ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-primary/40"} border-slate-200`}>
          {value.length === 0 ? (
            <span className="text-slate-400">Seleziona uno o più servizi…</span>
          ) : value.map((slug) => {
            const s = options.find((o) => o.slug === slug); if (!s) return null;
            return (
              <span key={slug} className="inline-flex items-center gap-1 rounded-full bg-[#EDEAF6] border border-[#D6D0EA] text-primary text-xs font-medium px-2 py-0.5">
                {serviceDisplayName(s)}
                {!disabled && (
                  <span role="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(slug); }}
                    className="hover:text-destructive"><X className="h-3 w-3" /></span>
                )}
              </span>
            );
          })}
          <ChevronDown className="h-4 w-4 text-slate-400 ml-auto shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-1 w-64" align="start">
        {options.length === 0 ? (
          <p className="text-sm text-slate-500 p-2">Nessun servizio disponibile per questo appalto.</p>
        ) : options.map((s) => (
          <label key={s.slug} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer text-sm">
            <Checkbox checked={value.includes(s.slug)} data-testid={`${testid}-opt-${s.slug}`} onCheckedChange={() => toggle(s.slug)} />
            <span className="text-slate-700">{serviceDisplayName(s)}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function MultiUserImport({ onClose }) {
  const [appalti, setAppalti] = useState([]);
  const [schools, setSchools] = useState([]);
  const [ceSites, setCeSites] = useState([]);
  const [appaltoId, setAppaltoId] = useState("");
  const [services, setServices] = useState([]);
  const [rows, setRows] = useState(() => Array.from({ length: 5 }, emptyRow));
  const [addN, setAddN] = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirm, setConfirm] = useState(null); // {title, desc, apply}
  const [bulkField, setBulkField] = useState("");
  const [bulkValue, setBulkValue] = useState("");

  useEffect(() => {
    api.get("/my-appalti").then((r) => {
      setAppalti(r.data);
      if (r.data.length === 1) setAppaltoId(r.data[0].id);
    }).catch(() => {});
    api.get("/admin/schools").then((r) => setSchools(r.data)).catch(() => {});
    api.get("/admin/operative-sites").then((r) => setCeSites(r.data)).catch(() => {});
  }, []);

  const appalto = appalti.find((a) => a.id === appaltoId);
  const serviceOptions = useMemo(
    () => (appalto?.servizi || []).filter((s) => s.stato === "attivo" && isUserService(s)),
    [appalto]
  );
  const columns = useMemo(() => deriveColumns(services), [services]);
  const configured = !!appaltoId && services.length > 0;

  // Opzioni dei dropdown relazionali, contestuali all'appalto selezionato.
  const relOptions = (relKind) => {
    if (relKind === "schools") {
      return schools
        .filter((s) => (s.appalto_ids || []).includes(appaltoId) && !s.hidden_for_new_entries)
        .map((s) => ({ id: s.id, label: `${s.nome_scuola}${s.plesso_istituto ? " · " + s.plesso_istituto : ""}` }));
    }
    if (relKind === "ce_sites") {
      return ceSites
        .filter((s) => s.appalto_id === appaltoId && (s.service_types || []).includes("centro_estivo") && !s.hidden_for_new_entries)
        .map((s) => ({ id: s.id, label: `${s.denominazione}${s.comune ? " · " + s.comune : ""}` }));
    }
    return [];
  };

  const rowHasData = (r) => columns.some((c) => {
    const v = r[c.key];
    return v !== "" && v !== undefined && v !== null;
  });
  const isEmptyRow = (r) => !rowHasData(r);
  const tableHasData = rows.some((r) => r.status !== "created" && rowHasData(r));

  // --- Validazione (rispecchia i controlli del form singolo / backend) ---
  const rowErrors = (r) => {
    if (r.status === "created") return {};
    if (isEmptyRow(r)) return {};
    const errs = {};
    columns.forEach((c) => {
      const req = typeof c.required === "function" ? c.required(r) : c.required !== false;
      const val = (r[c.key] ?? "").toString().trim();
      if (c.kind === "number") {
        if (val !== "" && parseHours(val) === null) errs[c.key] = "Valore non valido";
        else if (val !== "" && parseHours(val) < 0) errs[c.key] = "Non può essere negativo";
        return;
      }
      if (req && !val) { errs[c.key] = "Obbligatorio"; return; }
      if (c.kind === "email" && val && !EMAIL_RE.test(val)) errs[c.key] = "Email non valida";
    });
    return errs;
  };
  const errorsByRow = rows.map(rowErrors);
  const entered = rows.filter((r) => !isEmptyRow(r)).length;
  const createdCount = rows.filter((r) => r.status === "created").length;
  const readyIdx = rows.map((r, i) => ({ r, i, e: errorsByRow[i] }))
    .filter(({ r, e }) => !isEmptyRow(r) && r.status !== "created" && Object.keys(e).length === 0)
    .map(({ i }) => i);
  const ready = readyIdx.length;
  const toFix = rows.filter((r, i) => !isEmptyRow(r) && r.status !== "created" && Object.keys(errorsByRow[i]).length > 0).length;

  // --- Mutazioni righe ---
  const touch = (row) => ({ ...row, status: row.status === "created" ? row.status : "", error: "" });
  const setCell = (idx, key, value) => {
    setRows((prev) => prev.map((row, i) => (i === idx ? { ...touch(row), [key]: value } : row)));
    setResult(null);
  };
  const addRows = (n) => {
    const count = Math.max(1, Math.min(500, Math.floor(Number(n) || 0)));
    setRows((prev) => [...prev, ...Array.from({ length: count }, emptyRow)]);
    setResult(null);
  };
  const deleteRow = (idx) => {
    setRows((prev) => (prev.length <= 1 ? [emptyRow()] : prev.filter((_, i) => i !== idx)));
    setResult(null);
  };

  // --- Selezione righe + applicazione multipla ---
  const selectableIdx = rows.map((r, i) => (r.status === "created" ? -1 : i)).filter((i) => i >= 0);
  const selectedIdx = rows.map((r, i) => (r._sel && r.status !== "created" ? i : -1)).filter((i) => i >= 0);
  const allSelected = selectableIdx.length > 0 && selectableIdx.every((i) => rows[i]._sel);
  const toggleAll = () => setRows((prev) => prev.map((r) => (r.status === "created" ? r : { ...r, _sel: !allSelected })));
  const toggleRow = (idx) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, _sel: !r._sel } : r)));

  const dropdownCols = columns.filter((c) => isDropdownKind(c.kind));
  const bulkCol = dropdownCols.find((c) => c.key === bulkField);
  const applyBulk = () => {
    if (!bulkCol || bulkValue === "" || selectedIdx.length === 0) return;
    setRows((prev) => prev.map((r, i) => (r._sel && r.status !== "created" ? { ...touch(r), [bulkCol.key]: bulkValue } : r)));
    setResult(null);
    toast.success(`Valore applicato a ${selectedIdx.length} ${selectedIdx.length === 1 ? "riga" : "righe"}`);
  };

  // --- Copia/incolla (solo colonne testo/numero/email, come da requisito) ---
  const pasteCols = columns.filter((c) => isPasteKind(c.kind));
  const handlePaste = (e, rowIdx, pasteIdx) => {
    const text = e.clipboardData?.getData("text");
    if (!text) return;
    e.preventDefault();
    let lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
    while (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
    const parsed = lines.map((l) => l.split("\t"));
    setRows((prev) => {
      const next = prev.map((row) => ({ ...row }));
      const needed = rowIdx + parsed.length;
      while (next.length < needed) next.push(emptyRow());
      parsed.forEach((cells, ri) => {
        cells.forEach((val, ci) => {
          const col = pasteCols[pasteIdx + ci];
          if (!col) return;
          if (next[rowIdx + ri].status === "created") return;
          next[rowIdx + ri][col.key] = (val ?? "").trim();
          next[rowIdx + ri].status = ""; next[rowIdx + ri].error = "";
        });
      });
      return next;
    });
    setResult(null);
  };

  // --- Cambio Appalto / Servizi con conferma anti perdita dati ---
  const changeAppalto = (newId) => {
    if (newId === appaltoId) return;
    const apply = () => {
      setAppaltoId(newId);
      setServices([]);
      // Ripristina i campi collegati all'appalto/servizi; conserva i dati anagrafici.
      setRows((prev) => prev.map((r) => (r.status === "created" ? r : {
        ...emptyRow(), nome: r.nome || "", cognome: r.cognome || "", comune: r.comune || "",
      })));
      setBulkField(""); setBulkValue(""); setResult(null);
    };
    if (tableHasData || services.length > 0) {
      setConfirm({
        title: "Cambiare appalto?",
        desc: "Cambiando appalto verranno ripristinati i servizi e i campi collegati (scuola, sede, categoria, monte ore). I dati anagrafici già inseriti (Nome, Cognome, Comune) restano.",
        apply,
      });
    } else apply();
  };

  const changeServices = (next) => {
    const removed = services.filter((s) => !next.includes(s));
    const applied = () => {
      // Azzera nelle righe i valori delle colonne non più presenti.
      const keepKeys = new Set(deriveColumns(next).map((c) => c.key));
      setRows((prev) => prev.map((r) => {
        if (r.status === "created") return r;
        const nr = { ...r };
        columns.forEach((c) => { if (!keepKeys.has(c.key)) delete nr[c.key]; });
        return { ...touch(nr) };
      }));
      setServices(next);
      if (bulkField && !keepKeys.has(bulkField)) { setBulkField(""); setBulkValue(""); }
      setResult(null);
    };
    if (removed.length > 0) {
      const keepKeys = new Set(deriveColumns(next).map((c) => c.key));
      const droppedCols = columns.filter((c) => !keepKeys.has(c.key));
      const dataLost = rows.some((r) => r.status !== "created" && droppedCols.some((c) => {
        const v = r[c.key]; return v !== "" && v !== undefined && v !== null;
      }));
      if (dataLost) {
        setConfirm({
          title: "Rimuovere il servizio?",
          desc: "Alcune colonne collegate al servizio rimosso contengono dati che verranno eliminati. Vuoi procedere?",
          apply: applied,
        });
        return;
      }
    }
    applied();
  };

  // --- Creazione multipla (stessa API/logica del singolo: POST /admin/school-users) ---
  const buildPayload = (r) => {
    const has = (s) => services.includes(s);
    const p = {
      appalto_id: appaltoId, service_types: services,
      nome: (r.nome || "").trim(),
      cognome: (r.cognome || "").trim() || null,
      comune: (r.comune || "").trim() || null,
    };
    if (has("assistenza_scolastica")) { p.categoria_rendicontazione = r.categoria || null; p.school_id = r.school_id || null; }
    if (has("adm")) p.mail_privata_adm = (r.mail_privata_adm || "").trim() || null;
    const mps = {};
    if (has("assistenza_scolastica")) mps.assistenza_scolastica = r.mo_assistenza_scolastica ?? "";
    if (has("adm")) mps.adm = r.mo_adm ?? "";
    if (has("rest")) mps.rest = r.mo_rest ?? "";
    p.monte_ore_per_service = mps;
    if (has("centro_estivo")) {
      p.ce_tipologia = r.ce_tipologia || null;
      p.ce_site_id = r.ce_site_id || null;
      if (r.ce_tipologia === "utente") p.ce_monte_ore_settimanale = r.ce_monte_ore ?? "";
      else if (r.ce_tipologia === "gruppo") p.ce_monte_ore_giornaliero = r.ce_monte_ore ?? "";
    }
    return p;
  };

  const submit = async () => {
    if (ready === 0 || submitting) return;
    setSubmitting(true);
    let created = 0, error = 0;
    const updates = {};
    for (const idx of readyIdx) {
      try {
        await api.post("/admin/school-users", buildPayload(rows[idx]));
        updates[idx] = { status: "created", error: "" }; created += 1;
      } catch (err) {
        const detail = formatApiError(err.response?.data?.detail) || err.message || "Errore";
        updates[idx] = { status: "error", error: detail }; error += 1;
      }
    }
    setRows((prev) => prev.map((row, i) => (updates[i] ? { ...row, ...updates[i], _sel: false } : row)));
    setResult({ created, error });
    setSubmitting(false);
    if (created > 0) toast.success(`${created} ${created === 1 ? "utente creato" : "utenti creati"}`);
    if (error > 0) toast.error(`${error} ${error === 1 ? "riga non creata" : "righe non create"}`);
  };

  const statusBadge = (r) => {
    if (r.status === "created") return <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="mu-row-created"><CheckCircle2 className="h-3.5 w-3.5" />Creato</span>;
    if (r.status === "error") return <span className="inline-flex items-center gap-1 text-xs font-semibold text-destructive" data-testid="mu-row-error"><XCircle className="h-3.5 w-3.5" />Errore</span>;
    return null;
  };

  const renderCell = (row, r, col) => {
    const errs = errorsByRow[r];
    const locked = row.status === "created";
    const err = errs[col.key];
    const cls = `w-full h-9 px-2 rounded-md border text-sm bg-white outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${err ? "border-destructive bg-destructive/5" : "border-slate-200"} ${locked ? "text-slate-400" : "text-slate-800"}`;
    if (isDropdownKind(col.kind)) {
      const opts = col.kind === "relational" ? relOptions(col.relKind)
        : col.options.map((o) => (typeof o === "string" ? { id: o, label: o } : { id: o.v, label: o.l }));
      return (
        <>
          <Select value={row[col.key] || ""} onValueChange={(v) => setCell(r, col.key, v)} disabled={locked}>
            <SelectTrigger data-testid={`mu-cell-${r}-${col.key}`} className={`h-9 text-sm ${err ? "border-destructive" : "border-slate-200"}`}>
              <SelectValue placeholder="Seleziona…" />
            </SelectTrigger>
            <SelectContent>
              {opts.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-400">Nessuna opzione</div>
              ) : opts.map((o) => (<SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>))}
            </SelectContent>
          </Select>
          {err && <p className="text-[11px] text-destructive mt-0.5 px-1">{err}</p>}
        </>
      );
    }
    const pasteIdx = pasteCols.findIndex((c) => c.key === col.key);
    return (
      <>
        <input
          data-testid={`mu-cell-${r}-${col.key}`}
          disabled={locked}
          value={row[col.key] ?? ""}
          onChange={(e) => setCell(r, col.key, e.target.value)}
          onPaste={(e) => handlePaste(e, r, pasteIdx)}
          className={cls}
          title={err || ""}
        />
        {err && <p className="text-[11px] text-destructive mt-0.5 px-1">{err}</p>}
      </>
    );
  };

  return (
    <div data-testid="multi-user-import">
      <button data-testid="mu-back-btn" onClick={onClose}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline mb-4">
        <ArrowLeft className="h-4 w-4" /> Torna a Utenti e gruppi
      </button>

      <h2 className="font-heading text-xl font-bold text-slate-900 mb-1">Inserimento multiplo utenti</h2>
      <p className="text-sm text-slate-500 mb-4">Definisci Appalto e Servizi, poi compila la tabella o incolla da Excel / Google Sheets. Ogni riga valida crea un utente identico a "Crea utente".</p>

      {/* Fase 1: Appalto + Servizi */}
      <div className="mb-4 rounded-xl border border-slate-200 bg-white shadow-sm p-4 grid gap-4 sm:grid-cols-2" data-testid="mu-context">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-slate-600 block">Appalto <span className="text-destructive">*</span></label>
          <Select value={appaltoId} onValueChange={changeAppalto} disabled={appalti.length <= 1}>
            <SelectTrigger data-testid="mu-appalto" className="h-11"><SelectValue placeholder="Seleziona l'appalto…" /></SelectTrigger>
            <SelectContent>{appalti.map((a) => (<SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>))}</SelectContent>
          </Select>
          {appalti.length === 0 && <p className="text-xs text-amber-600">Nessun appalto attivo nel tuo perimetro.</p>}
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-slate-600 block">Servizi <span className="text-destructive">*</span></label>
          <ServicesPicker value={services} onChange={changeServices} options={serviceOptions} disabled={!appaltoId} testid="mu-services" />
          {appaltoId && serviceOptions.length === 0 && <p className="text-xs text-amber-600">Nessun servizio utente abilitato in questo appalto.</p>}
        </div>
      </div>

      {!configured ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500" data-testid="mu-need-context">
          Seleziona un appalto e almeno un servizio per generare la tabella.
        </div>
      ) : (
        <>
          <div className="mb-4 rounded-lg bg-[#EDEAF6] border border-[#D6D0EA] text-slate-700 text-sm px-3 py-2 flex items-start gap-2" data-testid="mu-paste-hint">
            <Info className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
            <span>Incolla (Ctrl/Cmd+V) più righe nei campi di testo. I campi a tendina (Scuola, Sede, Categoria…) si impostano dai menu o con l'applicazione multipla qui sotto.</span>
          </div>

          {/* Riepilogo conteggi */}
          <div className="mb-3 flex flex-wrap items-center gap-4 text-sm" data-testid="mu-summary">
            <span className="font-semibold text-slate-700">{entered} inseriti</span>
            <span className="text-emerald-600 font-semibold" data-testid="mu-ready-count">{ready} pronti</span>
            {toFix > 0 && <span className="text-amber-600 font-semibold" data-testid="mu-tofix-count">{toFix} da correggere</span>}
            {createdCount > 0 && <span className="text-slate-500">{createdCount} già creati</span>}
          </div>

          {/* Applicazione multipla ai campi a tendina */}
          {selectedIdx.length > 0 && (
            <div className="mb-3 rounded-xl border border-[#D6D0EA] bg-[#F5F2FB] p-3 flex flex-wrap items-end gap-3" data-testid="mu-bulk-bar">
              <span className="text-sm font-semibold text-primary">{selectedIdx.length} selezionate</span>
              {dropdownCols.length === 0 ? (
                <span className="text-sm text-slate-500">Nessuna colonna a tendina per i servizi scelti.</span>
              ) : (
                <>
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-slate-500 block">Campo</label>
                    <Select value={bulkField} onValueChange={(v) => { setBulkField(v); setBulkValue(""); }}>
                      <SelectTrigger data-testid="mu-bulk-field" className="h-9 w-48 text-sm bg-white"><SelectValue placeholder="Scegli campo…" /></SelectTrigger>
                      <SelectContent>{dropdownCols.map((c) => (<SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-slate-500 block">Valore</label>
                    <Select value={bulkValue} onValueChange={setBulkValue} disabled={!bulkCol}>
                      <SelectTrigger data-testid="mu-bulk-value" className="h-9 w-56 text-sm bg-white"><SelectValue placeholder="Scegli valore…" /></SelectTrigger>
                      <SelectContent>
                        {bulkCol && (bulkCol.kind === "relational"
                          ? relOptions(bulkCol.relKind)
                          : bulkCol.options.map((o) => (typeof o === "string" ? { id: o, label: o } : { id: o.v, label: o.l }))
                        ).map((o) => (<SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" size="sm" data-testid="mu-bulk-apply" disabled={!bulkCol || bulkValue === ""}
                    onClick={applyBulk} className="rounded-lg bg-white text-slate-700 border-slate-300 hover:bg-slate-50 h-9">
                    Applica alle righe selezionate
                  </Button>
                </>
              )}
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#F1EEF9] text-slate-600">
                  <th className="px-2 py-2 w-10">
                    <Checkbox checked={allSelected} data-testid="mu-select-all" onCheckedChange={toggleAll} />
                  </th>
                  <th className="px-2 py-2 text-left font-semibold w-10">#</th>
                  {columns.map((col) => {
                    const req = typeof col.required === "function" ? true : col.required !== false;
                    return (
                      <th key={col.key} className="px-2 py-2 text-left font-semibold min-w-[150px]">
                        {col.label}{req && <span className="text-destructive"> *</span>}
                      </th>
                    );
                  })}
                  <th className="px-2 py-2 text-left font-semibold min-w-[110px]">Stato</th>
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, r) => {
                  const locked = row.status === "created";
                  return (
                    <tr key={r} className={`border-t border-slate-100 ${locked ? "bg-emerald-50/40" : row.status === "error" ? "bg-amber-50/30" : ""}`} data-testid={`mu-row-${r}`}>
                      <td className="px-2 py-1 text-center align-middle">
                        <Checkbox checked={!!row._sel} disabled={locked} data-testid={`mu-select-${r}`} onCheckedChange={() => toggleRow(r)} />
                      </td>
                      <td className="px-2 py-1 text-slate-400 text-xs text-center">{r + 1}</td>
                      {columns.map((col) => (
                        <td key={col.key} className="px-1 py-1 align-top min-w-[150px]">{renderCell(row, r, col)}</td>
                      ))}
                      <td className="px-2 py-1 align-middle">
                        {statusBadge(row)}
                        {row.error && row.status === "error" && <p className="text-[11px] text-slate-500 mt-0.5 max-w-[160px]">{row.error}</p>}
                      </td>
                      <td className="px-1 py-1 text-center align-middle">
                        <button data-testid={`mu-delete-row-${r}`} onClick={() => deleteRow(r)}
                          className="text-slate-400 hover:text-destructive transition-colors p-1" title="Elimina riga">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm" data-testid="mu-add-row-btn" onClick={() => addRows(1)} className="rounded-lg">
              <Plus className="h-4 w-4 mr-1.5" /> Aggiungi riga
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Numero righe</span>
              <input type="number" min={1} max={500} value={addN} data-testid="mu-add-n-input"
                onChange={(e) => setAddN(e.target.value)}
                className="w-20 h-9 px-2 rounded-md border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              <Button variant="outline" size="sm" data-testid="mu-add-n-btn" onClick={() => addRows(addN)} className="rounded-lg">
                Aggiungi
              </Button>
            </div>
          </div>

          {result && (
            <div className="mt-5 rounded-xl border border-slate-200 bg-white shadow-sm p-4" data-testid="mu-result-banner">
              <p className="font-heading font-bold text-slate-900 mb-2">Inserimento completato</p>
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold"><CheckCircle2 className="h-4 w-4" />{result.created} creati</span>
                {result.error > 0 && <span className="inline-flex items-center gap-1.5 text-destructive font-semibold"><XCircle className="h-4 w-4" />{result.error} non creati</span>}
              </div>
              {result.error > 0 && <p className="text-xs text-slate-500 mt-2">Le righe non create restano modificabili: correggile e premi di nuovo "Crea utenti". Gli utenti già creati non verranno ricreati.</p>}
            </div>
          )}

          <div className="mt-6 sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent pt-4 pb-2 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold text-slate-700" data-testid="mu-ready-label">{ready} {ready === 1 ? "utente pronto" : "utenti pronti"}</p>
            <Button data-testid="mu-submit-btn" onClick={submit} disabled={ready === 0 || submitting} className="rounded-lg font-semibold">
              <Send className="h-4 w-4 mr-2" />{submitting ? "Creazione in corso…" : `Crea ${ready} ${ready === 1 ? "utente" : "utenti"}`}
            </Button>
          </div>
        </>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="mu-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mu-confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="mu-confirm-ok" onClick={() => { confirm?.apply?.(); setConfirm(null); }}>Procedi</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
